import { API_ROOT } from '../config/ApiConfig';
import { generateRequestHeaders } from '../service/ApiService';
import { OdDiscount, OdDiscountCreateRequest, OdTemplate } from '../models/OdDiscount';

export interface OdDiscountApiResponse<T> {
    httpstatus: number;
    data?: T;
    message?: string;
    reason?: string;
}

/**
 * /v1/od-discounts is scoped by the x-distributor-org-id header (per LLD), unlike the
 * existing promotions/getAll endpoint which uses a distributor-org-id query param.
 */
function odDiscountHeaders(partnerId: string) {
    return {
        ...generateRequestHeaders(),
        'x-distributor-org-id': partnerId
    };
}

async function parseJsonBody(response: Response): Promise<any> {
    try {
        return await response.json();
    } catch (e) {
        console.error('Failed to parse od-discounts response body as JSON', e);
        return undefined;
    }
}

export async function getOdDiscountTemplates(
    partnerId: string,
    marketSegment: string
): Promise<OdDiscountApiResponse<OdTemplate[]>> {
    const response = await fetch(
        `${API_ROOT}/od-discounts/templates?marketSegment=${encodeURIComponent(marketSegment)}`,
        {
            headers: odDiscountHeaders(partnerId),
            method: 'GET'
        }
    );
    const body = await parseJsonBody(response);
    if (!response.ok) {
        return {
            httpstatus: response.status,
            message: body?.message || 'Failed to fetch flex discount templates',
            reason: body?.reason
        };
    }
    return { httpstatus: response.status, data: body as OdTemplate[] };
}

export async function getOdDiscounts(partnerId: string): Promise<OdDiscountApiResponse<OdDiscount[]>> {
    const response = await fetch(`${API_ROOT}/od-discounts`, {
        headers: odDiscountHeaders(partnerId),
        method: 'GET'
    });
    const body = await parseJsonBody(response);
    if (!response.ok) {
        return {
            httpstatus: response.status,
            message: body?.message || 'Failed to fetch closed discount codes',
            reason: body?.reason
        };
    }
    return { httpstatus: response.status, data: body as OdDiscount[] };
}

export async function createOdDiscount(
    partnerId: string,
    request: OdDiscountCreateRequest
): Promise<OdDiscountApiResponse<OdDiscount>> {
    const response = await fetch(`${API_ROOT}/od-discounts`, {
        headers: odDiscountHeaders(partnerId),
        method: 'POST',
        body: JSON.stringify(request)
    });
    const body = await parseJsonBody(response);
    if (!response.ok) {
        return {
            httpstatus: response.status,
            message: body?.message || 'Failed to create closed discount code',
            reason: body?.reason
        };
    }
    return { httpstatus: response.status, data: body as OdDiscount };
}

export async function deleteOdDiscount(partnerId: string, code: string): Promise<OdDiscountApiResponse<void>> {
    const response = await fetch(`${API_ROOT}/od-discounts/${encodeURIComponent(code)}`, {
        headers: odDiscountHeaders(partnerId),
        method: 'DELETE'
    });
    if (!response.ok) {
        const body = await parseJsonBody(response);
        return {
            httpstatus: response.status,
            message: body?.message || 'Failed to delete closed discount code',
            reason: body?.reason
        };
    }
    return { httpstatus: response.status };
}
